/* eslint-disable no-unused-vars */
import type {
  CindroApp,
  CindroOrganization,
  CindroSessionUser
} from '@cindro/contracts';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import {
  getReleaseStorageConfig,
  listOrganizationProjectEnvironments,
  listOrganizationProjects,
  listOrganizations,
  listReleaseAppVersions,
  listReleaseApps,
  listReleaseArtifacts,
  listReleaseChannels,
  listReleasePromotions,
  listReleaseShipments,
  listPipelineRuns,
  listStageRuns,
  listProjectRepoMappings,
  listWorkJourneys,
  listPipelineDefinitions,
  listDeliveryCandidates,
  listDeliveryCampaigns,
  listRuntimeConfigs,
  listTenantApiKeys,
  listIngestionSources,
  listOnCallSchedules,
  listEscalationPolicies,
  type ReleaseArtifactRow,
  type ReleaseAppVersionRow,
  type ReleaseChannelRow,
  type ReleasePromotionRow,
  type ReleaseStorageConfigRow
} from '../api';
import { appProductsLabel, normalizeAppProducts } from '../app-products';
import {
  CHECKLIST_DISMISSED_KEY,
  isWorkspaceAdminPage,
  isWorkspaceScopedPage,
  pageLookup
} from '../config/pages';
import type {
  ActivityOption,
  MetricOption,
  MemberOption,
  OrganizationOption,
  PageId,
  ProjectOption,
  ServiceOption
} from '../types/navigation';
import type { WorkspaceState } from '../types/workspace';
import { initialsFrom } from '../utils/formatters';
import {
  buildWorkspaceActivity,
  buildWorkspaceMetrics,
  buildWorkspaceServices
} from '../utils/workspace-summary';
import {
  preferredProjectIdForOrganization,
  readWorkspaceSelection,
  resolveProjectSelectionId,
  writeWorkspaceSelection
} from '../utils/workspace-selection';
import {
  readHiddenEnvironmentSlugs,
  writeEnvironmentVisibility
} from '../utils/environment-visibility';
import { useSession } from './SessionContext';
import {
  buildGuidePath,
  buildWorkspaceModulePath,
  buildWorkspaceAdminPath,
  buildWorkspacePath,
  readRouteFromPath
} from '../utils/routes';

function organizationToOption(
  organization: CindroOrganization
): OrganizationOption {
  return {
    id: organization.tenantOrganizationId,
    slug: organization.slug,
    name: organization.name,
    subtitle: `${organization.region.toUpperCase()} · ${organization.dataPlaneMode.toUpperCase()}`,
    mark: initialsFrom(organization.name)
  };
}

function appToOption(app: CindroApp): ProjectOption {
  const products = normalizeAppProducts(app.enabledProducts);
  return {
    id: app.tenantAppId,
    organizationId: app.tenantOrganizationId,
    slug: app.slug,
    name: app.name,
    platform: appProductsLabel(products),
    products,
    status: app.isActive ? 'ready' : 'draft',
    mark: initialsFrom(app.name)
  };
}

const emptyWorkspace: WorkspaceState = {
  organizations: [],
  apps: [],
  releaseApps: [],
  releaseAppVersions: [],
  environments: [],
  members: [],
  apiKeys: [],
  artifacts: [],
  channels: [],
  promotions: [],
  shipments: [],
  runtimeConfigs: [],
  storageConfig: null,
  pipelineRuns: [],
  stageRuns: [],
  projectRepoMappings: [],
  workJourneys: [],
  pipelineDefinitions: [],
  deliveryCandidates: [],
  deliveryCampaigns: [],
  ingestionSources: [],
  onCallSchedules: [],
  escalationPolicies: []
};

type WorkspaceContextValue = {
  workspace: WorkspaceState;
  workspaceLoading: boolean;
  workspaceError: string | null;
  hasLoadedWorkspaceOnce: boolean;
  workspaceVersion: number;
  workspaceBootstrapOnly: boolean;
  activeOrgId: string;
  activeProjectId: string;
  activeReleaseAppId: string;
  activeReleaseAppVersionId: string;
  activeEnvId: string;
  activePage: PageId;
  activeOrganization: OrganizationOption | undefined;
  activeProject: ProjectOption | undefined;
  activeEnvironment: ReturnType<typeof useWorkspaceEnv>;
  activeReleaseApp: ReleaseAppRow | null;
  activeReleaseAppVersion: ReleaseAppVersionRow | null;
  selectedAppVersions: ReleaseAppVersionRow[];
  releaseVersionOptions: ReleaseAppVersionRow[];
  visibleEnvironments: import('../api').EnvironmentRow[];
  hiddenEnvironmentSlugs: Set<string>;
  isEnvironmentVisible: (slug: string) => boolean;
  setEnvironmentVisible: (slug: string, visible: boolean) => void;
  channels: import('../api').ReleaseChannelRow[];
  organizationById: Map<string, OrganizationOption>;
  projectById: Map<string, ProjectOption>;
  environmentById: Map<string, import('../api').EnvironmentRow>;
  releaseAppById: Map<string, ReleaseAppRow>;
  releaseAppVersionById: Map<string, ReleaseAppVersionRow>;
  sortedArtifacts: ReleaseArtifactRow[];
  sortedPromotions: ReleasePromotionRow[];
  artifactById: Map<string, ReleaseArtifactRow>;
  latestPromotionByLane: Map<string, ReleasePromotionRow>;
  metrics: MetricOption[];
  activity: ActivityOption[];
  services: ServiceOption[];
  members: MemberOption[];
  checklistDismissed: boolean;
  setChecklistDismissed: (v: boolean) => void;
  setActiveOrgId: (id: string) => void;
  setActiveProjectId: (id: string) => void;
  setActiveReleaseAppId: (id: string) => void;
  setActiveReleaseAppVersionId: (id: string) => void;
  setActiveEnvId: (id: string) => void;
  setActivePage: (id: PageId) => void;
  selectOrganization: (id: string) => void;
  selectApp: (id: string) => void;
  selectEnvironment: (id: string) => void;
  selectNav: (id: PageId) => void;
  refreshWorkspace: () => void;
  handleRefreshWorkspace: () => void;
  setWorkspaceVersion: React.Dispatch<React.SetStateAction<number>>;
  // Menu state (owned here so AppShell can close them all)
  appMenuOpen: boolean;
  orgMenuOpen: boolean;
  workspaceMenuOpen: boolean;
  userMenuOpen: boolean;
  envMenuOpen: boolean;
  setAppMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setOrgMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setWorkspaceMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setUserMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setEnvMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
  closeMenus: () => void;
};

import type { ReleaseAppRow } from '../api';

// Small helper extracted for type inference
function useWorkspaceEnv(
  environments: import('../api').EnvironmentRow[],
  activeEnvId: string
) {
  return useMemo(
    () =>
      environments.find((env) => env.tenantEnvironmentId === activeEnvId) ??
      environments[0],
    [environments, activeEnvId]
  );
}

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);

type WorkspaceProviderProps = {
  children: React.ReactNode;
  onNavigate: (path: string, opts?: { replace?: boolean }) => void;
};

function WorkspaceProvider({ children, onNavigate }: WorkspaceProviderProps) {
  const { session, setWorkspaceVersion: sessionSetWorkspaceVersion } =
    useSession();

  const initialWorkspaceSelection = useMemo(() => readWorkspaceSelection(), []);

  const [activeOrgId, setActiveOrgId] = useState(
    initialWorkspaceSelection.activeOrgId
  );
  const [activeProjectId, setActiveProjectId] = useState(
    initialWorkspaceSelection.activeProjectId
  );
  const [activeReleaseAppId, setActiveReleaseAppId] = useState(
    initialWorkspaceSelection.activeReleaseAppId
  );
  const [activeReleaseAppVersionId, setActiveReleaseAppVersionId] = useState(
    initialWorkspaceSelection.activeReleaseAppVersionId
  );
  const [activeEnvId, setActiveEnvId] = useState(
    initialWorkspaceSelection.activeEnvId
  );
  const [activePage, setActivePage] = useState<PageId>('overview' as PageId);
  const [workspaceLoading, setWorkspaceLoadingRaw] = useState(true);
  const [workspaceError, setWorkspaceError] = useState<string | null>(null);
  const [hasLoadedWorkspaceOnce, setHasLoadedWorkspaceOnce] = useState(false);
  const setWorkspaceLoading = useCallback((next: boolean) => {
    setWorkspaceLoadingRaw(next);
    if (!next) {
      setHasLoadedWorkspaceOnce(true);
    }
  }, []);
  const [workspaceVersion, setWorkspaceVersion] = useState(0);
  const [environmentVisibilityRevision, setEnvironmentVisibilityRevision] =
    useState(0);
  const [workspaceBootstrapOnly, setWorkspaceBootstrapOnly] = useState(false);
  const [workspace, setWorkspace] = useState<WorkspaceState>(emptyWorkspace);
  const [checklistDismissed, setChecklistDismissed] = useState(
    () => localStorage.getItem(CHECKLIST_DISMISSED_KEY) === '1'
  );
  const [appMenuOpen, setAppMenuOpen] = useState(false);
  const [orgMenuOpen, setOrgMenuOpen] = useState(false);
  const [workspaceMenuOpen, setWorkspaceMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [envMenuOpen, setEnvMenuOpen] = useState(false);

  const organizations = workspace.organizations;
  const apps = workspace.apps;
  const releaseApps = workspace.releaseApps;
  const releaseAppVersions = workspace.releaseAppVersions;
  const environments = workspace.environments;
  const artifacts = workspace.artifacts;
  const promotions = workspace.promotions;
  const runtimeConfigs = workspace.runtimeConfigs;
  const apiKeys = workspace.apiKeys;
  const currentUser = session?.user ?? null;

  const hiddenEnvironmentSlugs = useMemo(
    () =>
      readHiddenEnvironmentSlugs(localStorage, activeOrgId, activeProjectId),
    [activeOrgId, activeProjectId, environmentVisibilityRevision]
  );

  const visibleEnvironments = useMemo(
    () =>
      environments.filter(
        (environment) => !hiddenEnvironmentSlugs.has(environment.slug)
      ),
    [environments, hiddenEnvironmentSlugs]
  );

  const channels = useMemo(
    () =>
      workspace.channels.filter(
        (channel) => !hiddenEnvironmentSlugs.has(channel.environment)
      ),
    [hiddenEnvironmentSlugs, workspace.channels]
  );

  const setEnvironmentVisible = useCallback(
    (slug: string, visible: boolean) => {
      const nextHidden = readHiddenEnvironmentSlugs(
        localStorage,
        activeOrgId,
        activeProjectId
      );
      if (visible) {
        nextHidden.delete(slug);
      } else {
        nextHidden.add(slug);
      }
      writeEnvironmentVisibility(
        localStorage,
        activeOrgId,
        activeProjectId,
        nextHidden
      );
      setEnvironmentVisibilityRevision((revision) => revision + 1);
    },
    [activeOrgId, activeProjectId]
  );

  const isEnvironmentVisible = useCallback(
    (slug: string) => !hiddenEnvironmentSlugs.has(slug),
    [hiddenEnvironmentSlugs]
  );

  const activeOrganization = useMemo(
    () =>
      organizations.find((org) => org.id === activeOrgId) ?? organizations[0],
    [organizations, activeOrgId]
  );

  const activeProject = useMemo(
    () => apps.find((app) => app.id === activeProjectId) ?? apps[0],
    [apps, activeProjectId]
  );

  const activeEnvironment = useMemo(
    () =>
      visibleEnvironments.find(
        (env) => env.tenantEnvironmentId === activeEnvId
      ) ?? visibleEnvironments[0],
    [visibleEnvironments, activeEnvId]
  );

  const activeReleaseApp = useMemo(
    () =>
      releaseApps.find((ra) => ra.releaseAppId === activeReleaseAppId) ??
      releaseApps[0] ??
      null,
    [releaseApps, activeReleaseAppId]
  );

  const activeReleaseAppVersion = useMemo(
    () =>
      releaseAppVersions.find(
        (v) => v.releaseAppVersionId === activeReleaseAppVersionId
      ) ??
      releaseAppVersions.find(
        (v) => v.releaseAppId === activeReleaseApp?.releaseAppId
      ) ??
      releaseAppVersions[0] ??
      null,
    [
      releaseAppVersions,
      activeReleaseApp?.releaseAppId,
      activeReleaseAppVersionId
    ]
  );

  const selectedAppVersions = useMemo(
    () =>
      activeReleaseApp
        ? releaseAppVersions.filter(
            (v) => v.releaseAppId === activeReleaseApp.releaseAppId
          )
        : [],
    [activeReleaseApp, releaseAppVersions]
  );

  const releaseVersionOptions =
    selectedAppVersions.length > 0 ? selectedAppVersions : releaseAppVersions;

  const organizationById = useMemo(
    () => new Map(organizations.map((org) => [org.id, org])),
    [organizations]
  );

  const projectById = useMemo(
    () => new Map(apps.map((app) => [app.id, app])),
    [apps]
  );

  const environmentById = useMemo(
    () => new Map(environments.map((env) => [env.tenantEnvironmentId, env])),
    [environments]
  );

  const releaseAppById = useMemo(
    () => new Map(releaseApps.map((ra) => [ra.releaseAppId, ra])),
    [releaseApps]
  );

  const releaseAppVersionById = useMemo(
    () => new Map(releaseAppVersions.map((v) => [v.releaseAppVersionId, v])),
    [releaseAppVersions]
  );

  const sortedArtifacts = useMemo(
    () =>
      [...artifacts].sort((left, right) => {
        const leftTime = new Date(
          left.buildGeneratedAt || left.created || left.updated || 0
        ).getTime();
        const rightTime = new Date(
          right.buildGeneratedAt || right.created || right.updated || 0
        ).getTime();
        return rightTime - leftTime;
      }),
    [artifacts]
  );

  const sortedPromotions = useMemo(
    () =>
      [...promotions].sort((left, right) => {
        const leftTime = new Date(left.created || left.updated || 0).getTime();
        const rightTime = new Date(
          right.created || right.updated || 0
        ).getTime();
        return rightTime - leftTime;
      }),
    [promotions]
  );

  const artifactById = useMemo(
    () => new Map(sortedArtifacts.map((a) => [a.releaseArtifactId, a])),
    [sortedArtifacts]
  );

  const latestPromotionByLane = useMemo(() => {
    const map = new Map<string, ReleasePromotionRow>();
    for (const promotion of sortedPromotions) {
      const key = `${promotion.environment}/${promotion.channel}`;
      if (!map.has(key)) {
        map.set(key, promotion);
      }
    }
    return map;
  }, [sortedPromotions]);

  const metrics = useMemo(
    () =>
      buildWorkspaceMetrics(
        artifacts,
        channels,
        runtimeConfigs,
        sortedPromotions
      ),
    [artifacts, channels, runtimeConfigs, sortedPromotions]
  );

  const activity = useMemo(
    () =>
      buildWorkspaceActivity(sortedPromotions, artifactById, sortedArtifacts),
    [artifactById, sortedArtifacts, sortedPromotions]
  );

  const services = useMemo(
    () => buildWorkspaceServices(apps, artifactById, channels, sortedArtifacts),
    [apps, artifactById, channels, sortedArtifacts]
  );

  const members = useMemo<MemberOption[]>(() => {
    if (!currentUser) {
      return [];
    }

    return [
      {
        name: currentUser.displayName,
        email: currentUser.email,
        role: currentUser.role,
        initials: initialsFrom(currentUser.displayName)
      }
    ];
  }, [currentUser]);

  // Persist workspace selection
  useEffect(() => {
    if (!session) {
      return;
    }

    writeWorkspaceSelection({
      activeOrgId,
      activeProjectId,
      activeReleaseAppId,
      activeReleaseAppVersionId,
      activeEnvId
    });
  }, [
    activeEnvId,
    activeOrgId,
    activeProjectId,
    activeReleaseAppId,
    activeReleaseAppVersionId,
    session
  ]);

  // Load organizations
  useEffect(() => {
    if (!session) {
      setWorkspace(emptyWorkspace);
      setWorkspaceBootstrapOnly(false);
      setWorkspaceError(null);
      setHasLoadedWorkspaceOnce(false);
      return;
    }

    let cancelled = false;
    setWorkspaceLoading(true);
    setWorkspaceError(null);

    (async () => {
      const token = session.accessToken;
      const orgRows = await listOrganizations(token);

      if (cancelled) return;

      const mappedOrganizations = orgRows.map(organizationToOption);
      const bootstrapOrganizations =
        mappedOrganizations.length === 1 &&
        mappedOrganizations[0]?.slug === 'default';
      const visibleOrganizations =
        mappedOrganizations.length > 1
          ? mappedOrganizations.filter((org) => org.slug !== 'default')
          : mappedOrganizations;

      const apiKeyRows =
        visibleOrganizations.length > 0 && !bootstrapOrganizations
          ? await listTenantApiKeys(token).catch(() => [])
          : [];

      // Merge, don't replace — this effect only owns organizations/apiKeys.
      // A full replacement here transiently zeroed apps/releaseApps/etc.
      // (real values are set by the "Load projects + workspace data" effect
      // below) — during that window requiresOnboarding briefly went true
      // (organizations.length > 0 && apps.length === 0), which unmounted
      // AppShell in favor of OnboardingPage and wiped all its local state
      // (open dialogs, forms, menus). Since this effect re-runs on every
      // workspaceVersion bump, that meant every create/update/delete action
      // anywhere in the app could silently close whatever dialog was open.
      setWorkspace((previous) => ({
        ...previous,
        organizations: visibleOrganizations,
        members: session ? [session.user as CindroSessionUser] : [],
        apiKeys: apiKeyRows
      }));

      const nextOrganization = mappedOrganizations.find(
        (org) => org.id === activeOrgId
      );

      if (mappedOrganizations.length === 0) {
        setWorkspaceBootstrapOnly(false);
        setWorkspaceLoading(false);
        return;
      }

      if (!nextOrganization && visibleOrganizations[0]) {
        setActiveOrgId(visibleOrganizations[0].id);
      }

      setWorkspaceBootstrapOnly(bootstrapOrganizations);

      if (visibleOrganizations.length === 0 || bootstrapOrganizations) {
        setWorkspaceLoading(false);
      }
    })().catch(() => {
      if (!cancelled) {
        setWorkspaceError('Cindro could not load your workspaces.');
        setWorkspaceLoadingRaw(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [session, workspaceVersion]);

  // Load projects + workspace data
  useEffect(() => {
    if (!session || organizations.length === 0) {
      return;
    }

    const currentRoute =
      typeof window !== 'undefined'
        ? readRouteFromPath(window.location.pathname)
        : undefined;
    const routeOrganization =
      currentRoute?.kind === 'workspace' && currentRoute.orgSlug
        ? organizations.find((org) => org.slug === currentRoute.orgSlug)
        : undefined;
    const activeOrganizationRecord =
      routeOrganization ??
      organizations.find((org) => org.id === activeOrgId) ??
      organizations[0];

    let cancelled = false;
    setWorkspaceLoading(true);
    setWorkspaceError(null);

    (async () => {
      const token = session.accessToken;

      const appRows = await listOrganizationProjects(
        token,
        activeOrganizationRecord.id
      );
      const mappedApps = appRows.map(appToOption);
      // `cindro` is a valid customer project slug. Hiding it here forced the
      // real Cindro project to be recreated as `cindro-2`, which is the source
      // of the confusing production route rather than a database constraint.
      const visibleApps = mappedApps;
      const routeProjectSlug =
        currentRoute?.kind === 'workspace' &&
        currentRoute.orgSlug === activeOrganizationRecord.slug
          ? currentRoute.projectSlug
          : '';
      const storedSelection = readWorkspaceSelection();
      const preferredProjectId = preferredProjectIdForOrganization(
        storedSelection,
        activeOrganizationRecord.id
      );
      const selectedProjectId = resolveProjectSelectionId(
        visibleApps,
        routeProjectSlug,
        preferredProjectId ||
          (activeOrgId === activeOrganizationRecord.id ? activeProjectId : '')
      );

      const environmentRowsPromise = selectedProjectId
        ? listOrganizationProjectEnvironments(
            token,
            activeOrganizationRecord.id,
            selectedProjectId
          )
        : Promise.resolve([]);

      const optionalWorkspaceLoad = async <T,>(
        resource: string,
        request: Promise<T>,
        fallback: T
      ): Promise<T> => {
        try {
          return await request;
        } catch (error) {
          console.error('[workspace] failed to load resource', {
            resource,
            organizationId: activeOrganizationRecord.id,
            projectId: selectedProjectId || null,
            error
          });
          return fallback;
        }
      };

      const [
        environmentRows,
        apiKeyRows,
        artifactRows,
        channelRows,
        promotionRows,
        shipmentRows,
        runtimeRows,
        storageRow,
        pipelineRunRows,
        stageRunRows,
        projectRepoMappingRows,
        workJourneyRows,
        pipelineDefinitionRows,
        deliveryCandidateRows,
        deliveryCampaignRows,
        ingestionSourceRows,
        onCallScheduleRows,
        escalationPolicyRows
      ] = await Promise.all([
        optionalWorkspaceLoad('environments', environmentRowsPromise, []),
        optionalWorkspaceLoad('api keys', listTenantApiKeys(token), []),
        optionalWorkspaceLoad(
          'release artifacts',
          listReleaseArtifacts(token, {
            organizationId: activeOrganizationRecord.id,
            projectId: selectedProjectId
          }),
          []
        ),
        optionalWorkspaceLoad(
          'release channels',
          listReleaseChannels(token, {
            organizationId: activeOrganizationRecord.id,
            projectId: selectedProjectId
          }),
          []
        ),
        optionalWorkspaceLoad(
          'release promotions',
          listReleasePromotions(token, {
            organizationId: activeOrganizationRecord.id,
            projectId: selectedProjectId
          }),
          []
        ),
        selectedProjectId
          ? optionalWorkspaceLoad(
              'release shipments',
              listReleaseShipments(token, {
                organizationId: activeOrganizationRecord.id,
                projectId: selectedProjectId
              }),
              []
            )
          : Promise.resolve([]),
        optionalWorkspaceLoad(
          'runtime configs',
          listRuntimeConfigs(token, {
            organizationId: activeOrganizationRecord.id,
            projectId: selectedProjectId
          }),
          []
        ),
        optionalWorkspaceLoad(
          'release storage',
          getReleaseStorageConfig(token),
          null
        ),
        optionalWorkspaceLoad(
          'pipeline runs',
          listPipelineRuns(token, {
            organizationId: activeOrganizationRecord.id
          }),
          []
        ),
        optionalWorkspaceLoad(
          'stage runs',
          listStageRuns(token, {
            organizationId: activeOrganizationRecord.id
          }),
          []
        ),
        optionalWorkspaceLoad(
          'repository mappings',
          listProjectRepoMappings(token, {
            organizationId: activeOrganizationRecord.id
          }),
          []
        ),
        optionalWorkspaceLoad(
          'work journeys',
          listWorkJourneys(token, {
            organizationId: activeOrganizationRecord.id
          }),
          []
        ),
        optionalWorkspaceLoad(
          'pipeline definitions',
          listPipelineDefinitions(token, activeOrganizationRecord.id),
          []
        ),
        optionalWorkspaceLoad(
          'delivery candidates',
          listDeliveryCandidates(token, {
            organizationId: activeOrganizationRecord.id
          }),
          []
        ),
        optionalWorkspaceLoad(
          'delivery campaigns',
          listDeliveryCampaigns(token, {
            organizationId: activeOrganizationRecord.id
          }),
          []
        ),
        optionalWorkspaceLoad(
          'ingestion sources',
          listIngestionSources(token, {
            organizationId: activeOrganizationRecord.id,
            projectId: selectedProjectId
          }),
          []
        ),
        optionalWorkspaceLoad(
          'on-call schedules',
          listOnCallSchedules(token, {
            organizationId: activeOrganizationRecord.id
          }),
          []
        ),
        optionalWorkspaceLoad(
          'escalation policies',
          listEscalationPolicies(token, {
            organizationId: activeOrganizationRecord.id
          }),
          []
        )
      ]);

      if (cancelled) return;

      const releaseAppRows = selectedProjectId
        ? await listReleaseApps(token, {
            organizationId: activeOrganizationRecord.id,
            projectId: selectedProjectId
          }).catch(() => [])
        : [];

      const releaseVersionRows = (
        await Promise.all(
          releaseAppRows.map((ra) =>
            listReleaseAppVersions(token, {
              organizationId: activeOrganizationRecord.id,
              projectId: selectedProjectId || '',
              releaseAppId: ra.releaseAppId
            }).catch(() => [])
          )
        )
      ).flat();

      setWorkspace((previous) => ({
        ...previous,
        apps: visibleApps,
        releaseApps: releaseAppRows,
        releaseAppVersions: releaseVersionRows,
        environments: environmentRows,
        apiKeys: apiKeyRows,
        artifacts: artifactRows,
        channels: channelRows,
        promotions: promotionRows,
        shipments: shipmentRows,
        runtimeConfigs: runtimeRows,
        storageConfig: storageRow,
        pipelineRuns: pipelineRunRows,
        stageRuns: stageRunRows,
        projectRepoMappings: projectRepoMappingRows,
        workJourneys: workJourneyRows,
        pipelineDefinitions: pipelineDefinitionRows,
        deliveryCandidates: deliveryCandidateRows,
        deliveryCampaigns: deliveryCampaignRows,
        ingestionSources: ingestionSourceRows,
        onCallSchedules: onCallScheduleRows,
        escalationPolicies: escalationPolicyRows,
        members: session ? [session.user as CindroSessionUser] : []
      }));

      const selectedProject =
        visibleApps.find((app) => app.id === selectedProjectId) ??
        visibleApps[0];
      if (selectedProject?.id !== activeProjectId) {
        setActiveProjectId(selectedProject?.id ?? '');
      }
      if (selectedProject) {
        const currentPath =
          typeof window !== 'undefined' ? window.location.pathname : '';
        const workspaceRoot = /^\/org\/[^/]+\/?$/.test(currentPath);
        const workspacePage = workspaceRoot
          ? 'applications'
          : currentPath.match(
              /\/org\/[^/]+\/(?:(?<page>inbox|work-radar|release-control|delivery-model|connections|agents|agents\/runners|guide)(?:\/[a-z0-9-]+)?|workspace\/(?<adminPage>[a-z0-9-]+))\/?$/
            )?.groups?.['page'];
        const workspaceAdminPage = currentPath
          ? currentPath.match(
              /\/org\/[^/]+\/workspace\/(?<adminPage>[a-z0-9-]+)\/?$/
            )?.groups?.['adminPage']
          : undefined;
        onNavigate(
          workspaceAdminPage && isWorkspaceAdminPage(workspaceAdminPage)
            ? buildWorkspaceAdminPath(
                activeOrganizationRecord.slug,
                workspaceAdminPage
              )
            : workspacePage
              ? workspacePage === 'guide'
                ? buildGuidePath(
                    activeOrganizationRecord.slug,
                    window.location.pathname.match(
                      /\/guide\/(?<articleId>[a-z0-9-]+)\/?$/i
                    )?.groups?.['articleId']
                  )
                : buildWorkspaceModulePath(
                    activeOrganizationRecord.slug,
                    workspacePage === 'inbox'
                      ? 'work-inbox'
                      : workspacePage === 'work-radar'
                        ? 'pipeline-runs'
                        : workspacePage === 'release-control'
                          ? 'shipments'
                          : workspacePage === 'agents/runners'
                            ? 'agent-runners'
                            : workspacePage === 'agents'
                              ? 'agent-work'
                              : workspacePage
                  )
              : buildWorkspacePath(
                  activeOrganizationRecord.slug,
                  selectedProject.slug,
                  activePage
                ),
          { replace: true }
        );
      } else {
        onNavigate('/onboarding', { replace: true });
      }

      const currentReleaseApp = releaseAppRows.find(
        (releaseApp) => releaseApp.releaseAppId === activeReleaseAppId
      );
      const firstReleaseAppWithVersions = releaseAppRows.find((releaseApp) =>
        releaseVersionRows.some(
          (version) => version.releaseAppId === releaseApp.releaseAppId
        )
      );
      const preferredReleaseApp =
        currentReleaseApp &&
        releaseVersionRows.some(
          (version) => version.releaseAppId === currentReleaseApp.releaseAppId
        )
          ? currentReleaseApp
          : (firstReleaseAppWithVersions ??
            currentReleaseApp ??
            releaseAppRows[0]);

      if (preferredReleaseApp?.releaseAppId !== activeReleaseAppId) {
        setActiveReleaseAppId(preferredReleaseApp?.releaseAppId ?? '');
      }

      const preferredVersions = preferredReleaseApp
        ? releaseVersionRows.filter(
            (version) =>
              version.releaseAppId === preferredReleaseApp.releaseAppId
          )
        : [];
      if (
        preferredVersions.length > 0 &&
        !preferredVersions.some(
          (version) => version.releaseAppVersionId === activeReleaseAppVersionId
        )
      ) {
        setActiveReleaseAppVersionId(preferredVersions[0].releaseAppVersionId);
      } else if (releaseAppRows.length === 0) {
        setActiveReleaseAppId('');
        setActiveReleaseAppVersionId('');
      }

      setWorkspaceBootstrapOnly(false);
      setWorkspaceLoading(false);
    })().catch(() => {
      if (!cancelled) {
        setWorkspaceError(
          'Cindro could not load the projects in this workspace.'
        );
        setWorkspaceLoadingRaw(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [
    activeProjectId,
    activeOrgId,
    onNavigate,
    organizations,
    session,
    workspaceVersion
  ]);

  const closeMenus = useCallback(() => {
    setAppMenuOpen(false);
    setOrgMenuOpen(false);
    setWorkspaceMenuOpen(false);
    setUserMenuOpen(false);
    setEnvMenuOpen(false);
  }, []);

  const selectNav = useCallback(
    (id: PageId) => {
      setActivePage(id);
      setAppMenuOpen(false);
      setOrgMenuOpen(false);
      setWorkspaceMenuOpen(false);
      setUserMenuOpen(false);
      setEnvMenuOpen(false);

      const org = organizations.find((item) => item.id === activeOrgId);
      const app = apps.find((item) => item.id === activeProjectId) ?? apps[0];

      if (org && app) {
        onNavigate(
          isWorkspaceScopedPage(id)
            ? buildWorkspaceModulePath(org.slug, id)
            : buildWorkspacePath(org.slug, app.slug, id)
        );
      }
    },
    [organizations, apps, activeOrgId, activeProjectId, onNavigate]
  );

  const selectApp = useCallback(
    (id: string) => {
      writeWorkspaceSelection({
        activeOrgId,
        activeProjectId: id,
        activeReleaseAppId: '',
        activeReleaseAppVersionId: '',
        activeEnvId: ''
      });
      setActiveProjectId(id);
      setActiveReleaseAppId('');
      setActiveReleaseAppVersionId('');
      setActiveEnvId('');
      setAppMenuOpen(false);
      setOrgMenuOpen(false);
      setUserMenuOpen(false);
      setEnvMenuOpen(false);

      const app = apps.find((item) => item.id === id);
      const org = organizations.find((item) => item.id === activeOrgId);

      if (app && org) {
        onNavigate(
          buildWorkspacePath(
            org.slug,
            app.slug,
            isWorkspaceScopedPage(activePage) ? 'overview' : activePage
          )
        );
        if (isWorkspaceScopedPage(activePage)) {
          setActivePage('overview');
        }
      }
    },
    [apps, organizations, activeOrgId, activePage, onNavigate]
  );

  const selectEnvironment = useCallback((id: string) => {
    setActiveEnvId(id);
    setEnvMenuOpen(false);
  }, []);

  const selectOrganization = useCallback(
    (id: string) => {
      const organization = organizations.find((item) => item.id === id);
      if (!organization) return;

      writeWorkspaceSelection({
        activeOrgId: id,
        activeProjectId: '',
        activeReleaseAppId: '',
        activeReleaseAppVersionId: '',
        activeEnvId: '',
        projectIdsByOrganization:
          readWorkspaceSelection().projectIdsByOrganization
      });
      setWorkspaceLoading(true);
      setActiveOrgId(id);
      setActiveReleaseAppId('');
      setActiveReleaseAppVersionId('');
      setActiveEnvId('');
      setOrgMenuOpen(false);
      setAppMenuOpen(false);
      setUserMenuOpen(false);
      setActiveProjectId('');
      setWorkspace((previous) => ({
        ...previous,
        apps: [],
        releaseApps: [],
        releaseAppVersions: [],
        environments: [],
        artifacts: [],
        channels: [],
        promotions: [],
        runtimeConfigs: [],
        pipelineRuns: [],
        stageRuns: [],
        projectRepoMappings: [],
        workJourneys: [],
        pipelineDefinitions: [],
        ingestionSources: [],
        onCallSchedules: [],
        escalationPolicies: []
      }));
      onNavigate(
        isWorkspaceScopedPage(activePage)
          ? buildWorkspaceModulePath(organization.slug, activePage)
          : buildWorkspacePath(organization.slug, 'switching', activePage),
        { replace: true }
      );
    },
    [activePage, onNavigate, organizations, setWorkspaceLoading]
  );

  const refreshWorkspace = useCallback(() => {
    setWorkspaceError(null);
    setWorkspaceLoadingRaw(true);
    setWorkspaceVersion((v) => v + 1);
  }, []);

  const handleRefreshWorkspace = refreshWorkspace;

  // Keep session context workspaceVersion in sync
  useEffect(() => {
    sessionSetWorkspaceVersion(workspaceVersion);
  }, [workspaceVersion, sessionSetWorkspaceVersion]);

  const value: WorkspaceContextValue = {
    workspace,
    workspaceLoading,
    workspaceError,
    hasLoadedWorkspaceOnce,
    workspaceVersion,
    workspaceBootstrapOnly,
    activeOrgId,
    activeProjectId,
    activeReleaseAppId,
    activeReleaseAppVersionId,
    activeEnvId,
    activePage,
    activeOrganization,
    activeProject,
    activeEnvironment,
    activeReleaseApp,
    activeReleaseAppVersion,
    selectedAppVersions,
    releaseVersionOptions,
    visibleEnvironments,
    hiddenEnvironmentSlugs,
    isEnvironmentVisible,
    setEnvironmentVisible,
    channels,
    organizationById,
    projectById,
    environmentById,
    releaseAppById,
    releaseAppVersionById,
    sortedArtifacts,
    sortedPromotions,
    artifactById,
    latestPromotionByLane,
    metrics,
    activity,
    services,
    members,
    checklistDismissed,
    setChecklistDismissed,
    setActiveOrgId,
    setActiveProjectId,
    setActiveReleaseAppId,
    setActiveReleaseAppVersionId,
    setActiveEnvId,
    setActivePage,
    selectOrganization,
    selectApp,
    selectEnvironment,
    selectNav,
    refreshWorkspace,
    handleRefreshWorkspace,
    setWorkspaceVersion,
    appMenuOpen,
    orgMenuOpen,
    workspaceMenuOpen,
    userMenuOpen,
    envMenuOpen,
    setAppMenuOpen,
    setOrgMenuOpen,
    setWorkspaceMenuOpen,
    setUserMenuOpen,
    setEnvMenuOpen,
    closeMenus
  };

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
}

function useWorkspace(): WorkspaceContextValue {
  const ctx = useContext(WorkspaceContext);
  if (!ctx)
    throw new Error('useWorkspace must be used within WorkspaceProvider');
  return ctx;
}

export { WorkspaceProvider, useWorkspace };
export type { WorkspaceContextValue };
